import { useMemo, type KeyboardEvent as ReactKeyboardEvent } from "react";
import useTranslation from "@/hooks/use_translation";
import {
  useDatePostedOptions,
  useEmploymentTypeOptions,
  useSalaryOptions,
  useWorkTypeOptions,
} from "@hooks/job_search/use_filter_options";
import { type RecentSearch } from "@/types/job_search/recent_searches";
import "./recent_searches_dropdown.scss";
import { Body, SecondaryBody } from "@sprout/typography.tsx";

type Props = {
  recentSearches: RecentSearch[];
  onSelect: (search: RecentSearch) => void;
};

const useFilterLabelLookup = () => {
  const datePostedOptions = useDatePostedOptions();
  const salaryOptions = useSalaryOptions();
  const workTypeOptions = useWorkTypeOptions();
  const employmentTypeOptions = useEmploymentTypeOptions();

  return useMemo(() => {
    const toMap = <T extends string>(options: { label: string; value: T }[]) =>
      Object.fromEntries(options.map((o) => [o.value, o.label])) as Record<T, string>;

    return {
      datePosted: toMap(datePostedOptions),
      salary: toMap(salaryOptions),
      workType: toMap(workTypeOptions),
      employmentType: toMap(employmentTypeOptions),
    };
  }, [datePostedOptions, salaryOptions, workTypeOptions, employmentTypeOptions]);
};

const buildTitle = (
  search: RecentSearch,
  t: (key: string, opts?: Record<string, string>) => string,
): string => {
  const query = search.query?.trim();
  const location = search.location?.trim();

  if (query && location) return t("recentSearches.titleWithLocation", { query, location });
  if (query) return query;
  if (location) return t("recentSearches.titleLocationOnly", { location });
  return "";
};

const buildFilterSummary = (
  search: RecentSearch,
  filterLabels: ReturnType<typeof useFilterLabelLookup>,
): string => {
  const entries: Array<{
    value: string | string[] | null | undefined;
    labels: Record<string, string>;
  }> = [
    { value: search.date_posted, labels: filterLabels.datePosted },
    { value: search.salary, labels: filterLabels.salary },
    { value: search.work_type, labels: filterLabels.workType },
    { value: search.employment_type, labels: filterLabels.employmentType },
  ];

  return entries
    .flatMap(({ value, labels }) => {
      const values = Array.isArray(value) ? value : value ? [value] : [];
      return values.map((v) => labels[v]).filter(Boolean);
    })
    .join(" • ");
};

export const RecentSearchesDropdown = ({ recentSearches, onSelect }: Props) => {
  const { t } = useTranslation("jobSearch");
  const filterLabels = useFilterLabelLookup();

  const recentSearchItems = useMemo(
    () =>
      recentSearches.map((search) => ({
        search,
        title: buildTitle(search, t),
        filterSummary: buildFilterSummary(search, filterLabels),
      })),
    [recentSearches, filterLabels, t],
  );

  if (recentSearches.length === 0) return null;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, search: RecentSearch) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(search);
    }
  };

  return (
    <div
      className="recent-searches-dropdown"
      role="listbox"
      aria-label={t("recentSearches.heading")}
    >
      <Body medium className="recent-searches-dropdown__heading">
        {t("recentSearches.heading")}
      </Body>
      {recentSearchItems.map(({ search, title, filterSummary }, index) => (
        <button
          key={`${search.last_searched_at}-${index}`}
          type="button"
          role="option"
          aria-selected="false"
          className="recent-searches-dropdown__item"
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(search);
          }}
          onKeyDown={(event) => handleKeyDown(event, search)}
        >
          <Body className="recent-searches-dropdown__title">{title}</Body>
          {filterSummary && (
            <SecondaryBody className="recent-searches-dropdown__filters">
              {filterSummary}
            </SecondaryBody>
          )}
        </button>
      ))}
    </div>
  );
};
